import React, { useState, useEffect, useMemo, useContext } from 'react';
import { ScrapbookFile, ScrapbookFolder, deleteFiles, copyFiles, moveFiles, downloadZip, getUploadSASs } from '@/api';
import { UserContext } from '@/components';
import { useNotification } from '@/components/general/NotificationProvider';
import SavedImageItem from './SavedImageItem';
import SavedReportItem from './SavedReportItem';
import FolderNote from './FolderNote';
import ColourChip from './ColourChip';
import ColourPalette from './ColourPalette';
import QuoteItem from './QuoteItem';
import StatItem from './StatItem';
import Tooltip from '@/components/general/Tooltip';
import ListLight from '@/assets/listLight.svg';
import SquaresFourLight from '@/assets/squaresFourLight.svg';
import SortDropdown from './SortDropdown';
import ArrowsClockWise from '@/assets/arrows-clockwise-light.svg';
import Upload from '@/assets/upload-simple-light.svg';
import Zip from '@/assets/file-zip-light.svg';
import SelectionAll from '@/assets/selection-all-light.svg';
import SelectionNone from '@/assets/selectionSlashLight.svg';
import Delete from '@/assets/trash-light.svg';
import MoveFolder from '@/assets/move-folder.svg';
import Copy from '@/assets/copy-simple-light.svg';
import { DeleteItemsDialog } from '../dialogs/DeleteItemsDialog';
import CopyMoveDialog from '../dialogs/CopyMoveDialog';
import UploadDialog from '../dialogs/UploadDialog';
import UploadFrame from '../dialogs/UploadFrame';
import UserFileItem from './UserFileItem';
import StylusAiItem from './StylusAiItem';
import FilterTabs from './FilterTabs';
import ContentPreview from '../previews/ContentPreview';
import StylusAiSummary from './StylusAiSummary';
import { ScrapbookContentType } from '@/types/ScrapbookModel';
import TechLaunchDatabaseFileItem from './TechLaunchDatabaseFileItem';
import AnalyticsBuilderItem from './AnalyticsBuilderItem';

interface ScrapbookContentProps {
  folderName: string;
  files?: ScrapbookFile[];
  loading?: boolean;
  onRefresh?: () => void;
  currentFolder: ScrapbookFolder;
  onNoteUpdate: (note: string | null) => void;
  onFilesUpdate?: (files: ScrapbookFile[]) => void;
  folders?: ScrapbookFolder[];
}

export function ScrapbookContent({
  folderName,
  files = [],
  loading,
  onRefresh,
  currentFolder,
  onNoteUpdate,
  onFilesUpdate,
  folders = [],
}: ScrapbookContentProps) {
  const user = useContext(UserContext);
  const { showNotification } = useNotification();
  const [sortBy, setSortBy] = useState('Newest');
  const [hasSelectedItems, setHasSelectedItems] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedFilter, setSelectedFilter] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [copyMoveMode, setCopyMoveMode] = useState<'copy' | 'move' | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showUploadFrame, setShowUploadFrame] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<any[]>([]);
  const [previewContent, setPreviewContent] = useState<ScrapbookFile | null>(null);

  const filteredFiles = useMemo(() => {
    let filtered = files;

    if (selectedFilter !== 0) {
      const filterOptions = [
        { id: 0, contentType: '' },
        { id: 1, contentType: ScrapbookContentType.Article },
        { id: 2, contentType: ScrapbookContentType.Image },
        { id: 3, contentType: ScrapbookContentType.Quote },
        { id: 4, contentType: ScrapbookContentType.Stat },
        { id: 5, contentType: ScrapbookContentType.ArticleExtract },
        { id: 6, contentType: ScrapbookContentType.Colour },
        { id: 7, contentType: ScrapbookContentType.ColourPalette },
        { id: 8, contentType: ScrapbookContentType.Blob },
        { id: 9, contentType: ScrapbookContentType.AIChat },
        { id: 10, contentType: ScrapbookContentType.AISummary },
        { id: 11, contentType: ScrapbookContentType.TechLaunchDatabase },
      ];

      const selectedFilterOption = filterOptions.find((f) => f.id === selectedFilter);
      if (selectedFilterOption) {
        if (selectedFilterOption.contentType === ScrapbookContentType.Blob) {
          filtered = files.filter((file) => file.isBlob);
        } else {
          filtered = files.filter((file) => file.contentType === selectedFilterOption.contentType);
        }
      }
    }

    return filtered.map((file) => ({
      ...file,
      sel: selectedFiles.has(file.id?.toString() || file.name),
    }));
  }, [files, selectedFiles, selectedFilter]);

  useEffect(() => {
    if (selectedFilter !== 0 && filteredFiles.length === 0 && files.length > 0) {
      setSelectedFilter(0);
    }
  }, [filteredFiles.length, files.length, selectedFilter]);

  useEffect(() => {
    setSelectedFiles(new Set());
    setHasSelectedItems(false);
  }, [currentFolder.id]);

  const sortedFiles = useMemo(() => {
    const sorted = [...filteredFiles];
    return sorted.sort((a, b) => {
      const dateA = new Date(a.lastModified || 0);
      const dateB = new Date(b.lastModified || 0);
      return sortBy === 'Oldest' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
    });
  }, [filteredFiles, sortBy]);

  const hasContent = files.length > 0;
  const showSelectAll = hasContent && selectedFiles.size === 0;
  const showSelectNone = hasContent && selectedFiles.size > 0;
  const showCopyItems = hasSelectedItems;
  const showMoveItems = hasSelectedItems && !currentFolder.isShared;

  // Utility function to find path by folder ID (similar to Vue's findByIdToPath)
  const findByIdToPath = (rootFolder: ScrapbookFolder, folderId: number): string => {
    if (!rootFolder) return currentFolder.path;

    const findFolderAndAncestors = (folder: ScrapbookFolder, targetId: number): ScrapbookFolder[] | null => {
      if (folder.id === targetId) return [folder];
      if (folder.children) {
        for (const child of folder.children) {
          const result = findFolderAndAncestors(child, targetId);
          if (result) return [folder, ...result];
        }
      }
      return null;
    };

    const ancestors = findFolderAndAncestors(rootFolder, folderId);
    return ancestors ? ancestors[ancestors.length - 1].path : currentFolder.path;
  };

  const downloadZipTooltipText = () => {
    const someFilesSelected = hasContent && selectedFiles.size > 0;
    return someFilesSelected ? 'Download files as .Zip' : 'Download folder as .Zip';
  };

  const itemSelect = (file: ScrapbookFile) => {
    const fileId = file.id?.toString() || file.name;
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
    setHasSelectedItems(newSelected.size > 0);
  };

  const selectAll = () => {
    const allFileIds = new Set(files.map((file) => file.id?.toString() || file.name));
    setSelectedFiles(allFileIds);
    setHasSelectedItems(true);
  };

  const selectNone = () => {
    setSelectedFiles(new Set());
    setHasSelectedItems(false);
  };

  const deleteItems = async () => {
    setShowDeleteDialog(false);
    try {
      const selectedFileObjects = files.filter((file) => selectedFiles.has(file.id?.toString() || file.name));
      const filesToDelete = selectedFileObjects.map((file) => ({
        name: file.name,
        isBlob: file.isBlob,
      }));

      const userId = user?.userId || '';

      const result = await deleteFiles(userId, currentFolder.id, currentFolder.path, filesToDelete);

      if (result.succeeded) {
        const remainingFiles = files.filter((file) => !selectedFiles.has(file.id?.toString() || file.name));
        onFilesUpdate?.(remainingFiles);
        selectNone();
        showNotification('Files deleted successfully', 'success');
      } else {
        showNotification(`Failed to delete files: ${result.message}`, 'error');
      }
    } catch {
      showNotification('Failed to delete files', 'error');
    }
  };

  const copyFilesClicked = () => {
    setCopyMoveMode('copy');
  };

  const moveFilesClicked = () => {
    setCopyMoveMode('move');
  };

  const handleCopyMoveOk = async (dstFolder: ScrapbookFolder) => {
    if (!dstFolder || dstFolder.id === currentFolder.id) {
      showNotification('Destination folder is same as source folder', 'error');
      setCopyMoveMode(null);
      return;
    }

    try {
      const selectedFileObjects = files.filter((file) => selectedFiles.has(file.id?.toString() || file.name));
      const filesToProcess = selectedFileObjects.map((file) => ({
        name: file.name,
        isBlob: file.isBlob,
        contentType: file.contentType,
      }));

      const userId = user?.userId || '';
      const isCopy = copyMoveMode === 'copy';

      const result = isCopy
        ? await copyFiles(userId, currentFolder.id, dstFolder.id, filesToProcess)
        : await moveFiles(userId, currentFolder.id, dstFolder.id, filesToProcess);

      if (result.succeeded) {
        if (!isCopy) {
          const remainingFiles = files.filter((file) => !selectedFiles.has(file.id?.toString() || file.name));
          onFilesUpdate?.(remainingFiles);
        }
        selectNone();
        showNotification(`${result.message} to ${result.folderMovedTo}`, 'success');
      } else if (result.alreadyExistingFileNames?.length) {
        // Unselect files that already exist in destination (matching Vue.js behavior)
        const existingFileNames = result.alreadyExistingFileNames;
        const newSelectedFiles = new Set(selectedFiles);

        files.forEach((file) => {
          if (existingFileNames.includes(file.name)) {
            const fileId = file.id?.toString() || file.name;
            newSelectedFiles.delete(fileId);
          }
        });

        setSelectedFiles(newSelectedFiles);
        setHasSelectedItems(newSelectedFiles.size > 0);
        showNotification('File(s) exist in destination; they have been unselected.', 'error');
      } else {
        showNotification(`Failed to ${isCopy ? 'copy' : 'move'} files: ${result.message}`, 'error');
      }
    } catch {
      showNotification(`Failed to ${copyMoveMode} files`, 'error');
    }

    setCopyMoveMode(null);
  };

  const downloadZipClick = async () => {
    if (!hasContent) return;

    try {
      const path = folders && folders[0] ? findByIdToPath(folders[0], currentFolder.id) : currentFolder.path;
      const selectedFileObjects = files.filter((file) => selectedFiles.has(file.id?.toString() || file.name));
      const filesList = selectedFileObjects.length > 0 ? selectedFileObjects : files; // If no selection, download all files

      const postObj = {
        path,
        userId: user?.userId || '',
        folderId: currentFolder.id,
        files: filesList.map((f) => ({ name: f.name })),
      };

      await downloadZip(postObj.userId, postObj.folderId, postObj.path, postObj.files, currentFolder.name);
    } catch (error) {
      console.error('Error downloading zip:', error);
    }
  };

  const getComponent = (file: ScrapbookFile) => {
    return file.contentType || 'Unknown';
  };

  const handleUploadFiles = async (fileList: FileList) => {
    setShowUploadDialog(false);

    try {
      const fileArray = Array.from(fileList);
      const userId = user?.userId || '';
      const fileNames = fileArray.map((f) => f.name);

      const response = await getUploadSASs(userId, currentFolder.id, currentFolder.path, fileNames);

      if (response.succeeded && response.uploadSASs) {
        const uploadFilesData = response.uploadSASs.map((sas) => {
          const fileItem = fileArray.find((f) => f.name === sas.file)!;
          return {
            name: sas.file,
            sas: sas.sas,
            fileItem,
            status: 'waiting' as const,
            bytesUploaded: 0,
          };
        });

        setUploadFiles(uploadFilesData);
        setShowUploadFrame(true);
      } else {
        showNotification('Failed to retrive upload data: ' + response.message, 'error');
      }
    } catch {
      showNotification('Failed to retrive upload data', 'error');
    }
  };

  const handleUploadFrameClose = () => {
    setShowUploadFrame(false);
    setUploadFiles([]);
    // Add delay to ensure server has processed the uploads
    setTimeout(() => {
      onRefresh?.();
    }, 500);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      await handleUploadFiles(droppedFiles);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <div className="relative flex-1 pt-3 pl-4">
      {loading && (
        <div className="theme--dark bg-opacity-75 absolute inset-0 z-50 flex items-center justify-center bg-[#212121]/46 transition-opacity">
          <div className="h-50 w-50 animate-spin rounded-full border-25 border-green-500 border-t-transparent"></div>
        </div>
      )}
      <h1 className="pb-2 text-2xl font-semibold text-gray-800">{folderName}</h1>
      <FolderNote currentFolder={currentFolder} onNoteUpdate={onNoteUpdate} />

      <div className="pt-4">
        <div className="mb-4 flex items-center justify-between">
          <FilterTabs files={files} selectedFilter={selectedFilter} onFilterChange={setSelectedFilter} />

          <div className="flex items-center gap-2">
            {showCopyItems && (
              <Tooltip content="Copy" placement="bottom" className="z-5">
                <Copy className="mr-2 h-7 w-7 cursor-pointer" onClick={copyFilesClicked} />
              </Tooltip>
            )}
            {showMoveItems && (
              <Tooltip content="Move" placement="bottom" className="z-5">
                <MoveFolder className="mr-2 h-7 w-7 cursor-pointer" onClick={moveFilesClicked} />
              </Tooltip>
            )}
            {hasSelectedItems && (
              <Tooltip content="Delete" placement="bottom" className="z-5">
                <Delete className="mr-2 h-7 w-7 cursor-pointer" onClick={() => setShowDeleteDialog(true)} />
              </Tooltip>
            )}
            {showSelectNone && (
              <Tooltip content="Select None" placement="bottom" className="z-5">
                <SelectionNone className="mr-2 h-7 w-7 cursor-pointer" onClick={selectNone} />
              </Tooltip>
            )}
            {showSelectAll && (
              <Tooltip content="Select All" placement="bottom" className="z-5">
                <SelectionAll className="mr-2 h-7 w-7 cursor-pointer" onClick={selectAll} />
              </Tooltip>
            )}
            {hasContent && (
              <Tooltip content={downloadZipTooltipText()} placement="bottom" className="z-5">
                <Zip className="mr-2 h-7 w-7 cursor-pointer" onClick={downloadZipClick} />
              </Tooltip>
            )}
            <Tooltip content="Upload files" placement="bottom" className="z-5">
              <Upload className="mr-2 h-7 w-7 cursor-pointer" onClick={() => setShowUploadDialog(true)} />
            </Tooltip>
            <Tooltip content="Refresh" placement="bottom" className="z-5">
              <ArrowsClockWise className="mr-2 h-7 w-7 cursor-pointer" onClick={onRefresh} />
            </Tooltip>
            {viewMode === 'grid' && (
              <span className="relative flex h-full w-8 items-center">
                <Tooltip content="List View" placement="bottom" className="z-5">
                  <ListLight className="mr-2 h-7 w-7 cursor-pointer" onClick={() => setViewMode('list')} />
                </Tooltip>
              </span>
            )}
            {viewMode === 'list' && (
              <span className="relative flex h-full w-8 items-center">
                <Tooltip content="Grid View" placement="bottom" className="z-5">
                  <SquaresFourLight className="mr-2 h-7 w-7 cursor-pointer" onClick={() => setViewMode('grid')} />
                </Tooltip>
              </span>
            )}

            <SortDropdown sortBy={sortBy} onSortChange={setSortBy} />
          </div>
        </div>

        {/* Content Area */}
        <hr role="separator" aria-orientation="horizontal" className="v-divider theme--light border-gray-200" />
        {hasContent ? (
          <div
            className={`my-2 flex-1 border-gray-200 ${
              viewMode === 'grid' ? 'xxl:grid-cols-4 grid grid-cols-2 gap-0 xl:grid-cols-3' : 'flex flex-col gap-0'
            }`}
            style={viewMode === 'grid' ? { gridAutoRows: 'min-content' } : {}}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            {sortedFiles.map((file, index) => (
              <div key={file.id?.toString() || file.name} className={viewMode === 'grid' ? 'w-full' : 'w-full'}>
                <div
                  className={`${!file.sel ? 'hover:bg-gray-2' : ''} group relative h-full w-full rounded-xl p-3 transition-all duration-200 ${
                    file.sel ? 'bg-gray-3' : 'border-white'
                  }`}
                  onClick={() => setPreviewContent(file)}
                >
                  {/* Selection checkbox */}
                  <div
                    className={`absolute top-5 left-5 z-10 flex w-6 cursor-pointer items-center justify-center transition-opacity duration-200 ${
                      file.sel ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      itemSelect(file);
                    }}
                  >
                    <div
                      className={`h-5 w-5 rounded-full border-2 transition-all duration-200 ${
                        file.sel ? 'border-black bg-white' : 'border-black group-hover:bg-white hover:border-black'
                      }`}
                    >
                      {file.sel && (
                        <div className="flex h-full w-full items-center justify-center">
                          <div className="h-2 w-2 rounded-full bg-black"></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* File content */}
                  <div className="">
                    {file.contentType === ScrapbookContentType.Article ? (
                      <SavedReportItem report={file} selected={file.sel || false} viewMode={viewMode} />
                    ) : file.contentType === ScrapbookContentType.Quote ? (
                      <QuoteItem content={file.content} file={file} viewMode={viewMode} />
                    ) : file.contentType === ScrapbookContentType.Stat ? (
                      <StatItem content={file.content} file={file} viewMode={viewMode} />
                    ) : file.contentType === ScrapbookContentType.Image ||
                      file.contentType === ScrapbookContentType.ArticleExtract ? (
                      <SavedImageItem
                        image={{
                          id: (file as any).id || parseInt(index.toString()),
                          name: file.name,
                          contentType: file.contentType,
                          content: file.content || {
                            title: file.content?.imageCaption || file.name,
                            thumbnailUrl: file.content?.imageUrl || file.content?.src || file.content?.thumbnailUrl,
                            description: file.content?.pageTitle || '',
                          },
                        }}
                        selected={file.sel || false}
                        onSelect={() => itemSelect(file)}
                        viewMode={viewMode}
                      />
                    ) : file.contentType === ScrapbookContentType.Colour ? (
                      <ColourChip content={file.content} file={file} viewMode={viewMode} />
                    ) : file.contentType === ScrapbookContentType.ColourPalette ? (
                      <ColourPalette
                        content={file.content}
                        file={file}
                        selected={file.sel || false}
                        onSelect={() => itemSelect(file)}
                        viewMode={viewMode}
                      />
                    ) : file.contentType === ScrapbookContentType.AIChat ? (
                      <StylusAiItem file={file} selected={file.sel || false} viewMode={viewMode} />
                    ) : file.contentType === ScrapbookContentType.AISummary ? (
                      <StylusAiSummary file={file} selected={file.sel || false} viewMode={viewMode} />
                    ) : file.contentType === ScrapbookContentType.Blob ? (
                      <UserFileItem
                        file={file}
                        selected={file.sel || false}
                        onSelect={() => itemSelect(file)}
                        viewMode={viewMode}
                      />
                    ) : file.contentType === ScrapbookContentType.TechLaunchDatabase ? (
                      <TechLaunchDatabaseFileItem
                        file={file}
                        selected={file.sel || false}
                        onSelect={() => itemSelect(file)}
                        viewMode={viewMode}
                      />
                    ) : file.contentType === ScrapbookContentType.AnalyticsBuilder ? (
                      <AnalyticsBuilderItem
                        file={file}
                        selected={file.sel || false}
                        onSelect={() => itemSelect(file)}
                        viewMode={viewMode}
                      />
                    ) : (
                      <div className="border-gray-3 rounded border bg-white p-3">
                        <div className="text-blue mb-2 text-lg font-bold">{getComponent(file)}</div>
                        <div className="truncate text-sm font-medium text-black">{file.name}</div>
                        <div className="mt-1 text-xs text-gray-600">
                          {new Date(file.lastModified || Date.now()).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="mt-66 flex h-full items-center justify-center text-xl text-gray-600"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            This folder is empty
          </div>
        )}
      </div>

      <ContentPreview content={previewContent} onClose={() => setTimeout(() => setPreviewContent(null), 200)} />

      <DeleteItemsDialog
        isOpen={showDeleteDialog}
        onConfirm={deleteItems}
        onCancel={() => setShowDeleteDialog(false)}
      />

      <CopyMoveDialog
        show={copyMoveMode !== null}
        folders={folders}
        onOk={handleCopyMoveOk}
        onCancel={() => setCopyMoveMode(null)}
      />
      <UploadDialog show={showUploadDialog} onOk={handleUploadFiles} onCancel={() => setShowUploadDialog(false)} />

      <UploadFrame
        open={showUploadFrame}
        files={uploadFiles}
        currentFolder={currentFolder}
        onClose={handleUploadFrameClose}
      />
    </div>
  );
}
